import { createFileRoute } from "@tanstack/react-router";
import { PageHeading } from "@/ui";
import { ActionSidebar } from "@/components/overview/ActionSidebar";
import { RecentPostsCard } from "@/components/overview/RecentPostsCard";
import { DashboardStats } from "@/components/overview/DashboardStats";
import { guardFreshWorkspaceOnboarding } from "@/lib/onboardingGuard";

// Dashboard (#639, replacing #592/#594's Overview): "Your blog at a
// glance. Stats, recent posts, and anything waiting on you." — supersedes
// D-592-4's "what needs me, and nothing else" framing (see DECISIONS.md).
// Lede reworded from "Your publication at a glance" (product feedback,
// 2026-08-13) to match the sidebar's Blog section noun.
export const Route = createFileRoute("/dashboard/w/$workspaceId/")({
  // Entry point for onboarding (#601): a fresh, never-onboarded workspace is
  // redirected to the three-screen wizard before the Dashboard ever renders.
  // See `guardFreshWorkspaceOnboarding` for what "fresh" means and why every
  // failure mode falls back to just showing the dashboard.
  beforeLoad: guardFreshWorkspaceOnboarding,
  component: Dashboard,
});

function Dashboard() {
  const { workspaceId } = Route.useParams();

  return (
    <div className="flex flex-col gap-6">
      <PageHeading
        title="Dashboard"
        lede="Your blog at a glance. Stats, recent posts, and anything waiting on you."
      />
      <DashboardStats workspaceId={workspaceId} />
      {/*
        One column on a phone, two from `md` up.

        `minmax(0, …)` rather than a bare `2fr 1fr` is the half that actually
        fixes it. An `fr` track takes an automatic minimum of `min-content`,
        and this one holds `RecentPostsCard`'s table — so it refused to shrink
        and pushed the second column off a 390px viewport, where `body`'s
        `overflow-x: hidden` clipped the ActionSidebar away with no scrollbar
        and no affordance (D-mobile-15). `minmax(0, …)` overrides that minimum,
        and the table then scrolls inside its own `overflow-x-auto` card.
        Without it the same clipping returns between `md` and roughly `lg`.
      */}
      <div className="grid items-start gap-6 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        {/*
          `ActionSidebar` FIRST in the DOM, deliberately — on a phone this grid
          is one column, and what is waiting on you must come before the posts
          table for every reader, not just the sighted one. It carries
          `md:col-start-2 md:row-start-1` to sit back in the right-hand column
          from `md` up; see its own comment for the trade-off that makes.
        */}
        <ActionSidebar workspaceId={workspaceId} />
        <RecentPostsCard workspaceId={workspaceId} />
      </div>
    </div>
  );
}
